import Vue from 'vue';

import membershipMethodKeys, { MAX_EXEMPTIONS_COUNT } from 'client/groups/Components/constants/MembershipConstants';

const { DYNAMIC_AUTOMATED } = membershipMethodKeys;

const getState = () => ({
  groupsToAddExemption: {
    toBind: [],
    toUnbind: [],
    toExceedExemptionLimit: [],
  },
});

// ---------------------
// ----- Item Aside -> Item Group tab, helper functions

/**
 * On the Device/User aside -> Device Groups/User Groups tab, when binding/unbinding groups
 * from that Item:
 *
 * For any newly bound/unbound Fully Dynamic groups, we need to add the current Device/User
 * (from the Device/User Aside we're on) to the exemption list for those newly bound/unbound
 * groups, as we're overriding their dynamic membership with a manual selection.
 *
 * The problem is, PATCH for groups does not work right now, so we need to do a PUT.
 * (meaning, we can't just send the updated exemptions list to an endpoint, we have to
 * pull the entire group, update their exemptions, and send the entire group back in a PUT)
 *
 * In order to do a PUT, we need the FULL group data set, which means we need to do a GET call for
 * every newly bound/unbound group, update the exemptions for each eligible group, and
 * then PUT those groups back.
 */

const SET_GROUPS_TO_ADD_EXEMPTION = (state, payload) => {
  Vue.set(state.features, 'groupsToAddExemption', payload);
};

/**
 * When saving the Device/User aside where groups have been newly bound/unbound,
 * we use the itemId of that item (from the Device/User Aside) to determine what
 * Fully Dynamic groups will have that item added as an exemption to their exemptions lists.
 *
 * First, we grab the ids of the newly bound/unbound and do a GET call for those groups.
 *
 * After that, we use that "fresh" group data to determine which groups we can add the
 * exemption to, which we can't, and which will be going over the exemption limit as a
 * result of that action.
 *
 * To do that, we split the "fresh" group data into three results lists
 * in `state.features.groupsToAddExemption` so that we can:
 *    1) pop the Exemptions Limit Exceeded/Conflict modal if needed
 *    2) modify those groups' exemptions lists, and send them back in a PUT request
 *
 * The reason why we don't just use the stored data in `state.entities.items` is
 * so that we can avoid race conditions when 2 admins are working on the same
 * Device/Device Group at once. Check the "Race Condition" section on the A/C here:
 * https://jumpcloud.atlassian.net/browse/HY-2506
 * @param {string} itemId id of the Device/User aside that's saving.
 */
const fetchAndSetGroupsToAddExemption = async (
  { commit, getters },
  { getGroupsByIdsFn, itemId },
) => {
  const mapItemToId = ({ id }) => id;

  const groupIdsToBind = getters.getItemsToBind.map(mapItemToId);
  const groupIdsToUnbind = getters.getItemsToUnbind.map(mapItemToId);

  const groupIdsToFetch = groupIdsToBind.concat(groupIdsToUnbind);
  const groupsDataResponse = await getGroupsByIdsFn(groupIdsToFetch);

  const groupIdsToBindSet = new Set(groupIdsToBind);

  /**
   * Of the groups that have been bound/unbound, we split those up into three lists:
   *  new bindings (toBind),
   *  new un-bindings (toUnbind), and
   *  any groups that will be exceeding the exemption limit (toExceedExemptionLimit)
   *
   * If the group is not Fully Dynamic, OR the item is already an exemption in any of
   * these groups, we don't include that group in the resulting arrays,
   * since we don't need to add the item as an exemption to these groups.
   *
   * We only want to pop the "Exemptions Limit Exceeded"/"Conflict" modals for the groups
   * that will have their exemptions lists affected by these bindings/unbindings.
   */
  const {
    toBind, toUnbind, toExceedExemptionLimit,
  } = (await groupsDataResponse).reduce((acc, group) => {
    const { membershipMethod, memberQueryExemptions = [] } = group;

    const exemptionAlreadyExists = memberQueryExemptions.find(({ id }) => id === itemId);

    if (membershipMethod !== DYNAMIC_AUTOMATED || exemptionAlreadyExists) return acc;

    if (memberQueryExemptions.length >= MAX_EXEMPTIONS_COUNT) {
      acc.toExceedExemptionLimit.push(group);
    } else if (!exemptionAlreadyExists) {
      const arrayToAddGroup = groupIdsToBindSet.has(group.id)
        ? 'toBind'
        : 'toUnbind';

      acc[arrayToAddGroup].push(group);
    }

    return acc;
  }, { toBind: [], toUnbind: [], toExceedExemptionLimit: [] });

  /**
   * Now that we have our three resulting arrays, we set them in state and
   * use the getters below for easy lookup.
   */
  commit('SET_GROUPS_TO_ADD_EXEMPTION', {
    toBind,
    toUnbind,
    toExceedExemptionLimit,
  });
};

const getGroupsToAddExemptionToBind = (state) => state.features.groupsToAddExemption.toBind;
const getGroupsToAddExemptionToUnbind = (state) => state.features.groupsToAddExemption.toUnbind;
const getGroupsToExceedExemptionLimit = (state) => state.features.groupsToAddExemption
  .toExceedExemptionLimit;

const addExemptionToGroups = async (context, {
  groups, itemId, exemptionType, updateCallback,
}) => {
  const newExemption = { type: exemptionType, id: itemId };

  const groupsToAddExemption = groups.map(group => ({
    ...group,
    memberQueryExemptions: [
      ...group.memberQueryExemptions,
      newExemption,
    ],
  }));

  const promises = groupsToAddExemption.map(updateCallback);
  await Promise.all(promises);
};

export {
  // ----- STATE
  getState,

  // ----- GETTERS
  getGroupsToAddExemptionToBind,
  getGroupsToAddExemptionToUnbind,
  getGroupsToExceedExemptionLimit,

  // ----- ACTIONS
  fetchAndSetGroupsToAddExemption,
  addExemptionToGroups,

  // ----- MUTATIONS
  SET_GROUPS_TO_ADD_EXEMPTION,
};
